import { useFormikContext } from "formik"
import { useMemo } from "react"
import { DateTime } from "luxon"

//Styles
import styles from "../../../../styles/Form.module.css"

const getStartOfDayMillis = (jsDate) => {
  return DateTime.fromJSDate(jsDate).startOf("day").toMillis()
}

export default function ManageHistoryOverlap({
  history,
  currentSelection,
  dataIdKey,
  dataNameKey,
}) {
  const formik = useFormikContext()

  const overlappingHistory = useMemo(() => {
    const currentDate = formik?.values?.manage_services_date

    if (!currentDate) return []

    return history.filter((historyItem) => {
      return (
        getStartOfDayMillis(historyItem.end_date) >=
          getStartOfDayMillis(currentDate) &&
        getStartOfDayMillis(historyItem.start_date) <
          getStartOfDayMillis(currentDate) &&
        !currentSelection.some((item) => item.key === historyItem[dataIdKey])
      )
    })
  }, [
    formik?.values?.manage_services_date,
    history,
    currentSelection,
    dataIdKey,
  ])

  const futureAllocationsToRemove = useMemo(() => {
    const currentDate = formik?.values?.manage_services_date

    if (!currentDate) return []

    const currentDateMilliseconds = getStartOfDayMillis(currentDate)

    return history.filter((historyItem) => {
      const startDateMilliseconds = getStartOfDayMillis(historyItem.start_date)
      const isHistoryItemSelected = currentSelection.some(
        (item) => item.key === historyItem[dataIdKey],
      )

      return (
        // If the start date of the history item is after the current date, and either:
        // - there are no current selections, or
        // - the history item is included in the current selections, then it will be removed.
        (startDateMilliseconds > currentDateMilliseconds &&
          (currentSelection.length === 0 || isHistoryItemSelected)) ||
        // If the start date of the history item is the same as the current date, but it is not included in the current selections, then it will be removed.
        (startDateMilliseconds === currentDateMilliseconds &&
          !isHistoryItemSelected)
      )
    })
  }, [
    currentSelection,
    dataIdKey,
    formik?.values?.manage_services_date,
    history,
  ])

  if (overlappingHistory.length > 0 || futureAllocationsToRemove.length > 0) {
    return (
      <div>
        {overlappingHistory.length > 0 && (
          <div className={styles.manageHistoryOverlapContainer}>
            <div className={styles.manageHistoryOverlapContainerIcon}>
              <i className="pi pi-exclamation-triangle"></i>
            </div>
            <div className={styles.manageHistoryOverlapContainerHeader}>
              These changes will end existing assignment entries on the change
              date.
            </div>
            <div className={styles.manageHistoryOverlapContainerList}>
              <ul>
                {overlappingHistory.map((historyItem) => (
                  <li key={historyItem[dataIdKey]}>
                    <span className={styles.manageHistoryOverlapListEntryKey}>
                      {historyItem[dataNameKey]}
                    </span>{" "}
                    {historyItem.start_date?.toLocaleDateString("en-US")}{" "}
                    {" to "}
                    {historyItem.end_date?.toLocaleDateString("en-US")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {futureAllocationsToRemove.length > 0 && (
          <div className={styles.manageHistoryOverlapContainer}>
            <div className={styles.manageHistoryOverlapContainerIcon}>
              <i className="pi pi-exclamation-triangle"></i>
            </div>
            <div className={styles.manageHistoryOverlapContainerHeader}>
              {`These changes will remove ${currentSelection.length > 0 ? "or replace " : ""} existing assignments.`}
            </div>
            <ul>
              {futureAllocationsToRemove.map((historyItem) => (
                <li key={historyItem[dataIdKey]}>
                  <span className={styles.manageHistoryOverlapListEntryKey}>
                    {historyItem[dataNameKey]}
                  </span>{" "}
                  {historyItem.start_date?.toLocaleDateString("en-US")} {" to "}
                  {historyItem.end_date?.toLocaleDateString("en-US")}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  } else {
    return null
  }
}
